import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LanguageContext";
import { formatPrice } from "@/lib/price";
import { X, ShoppingBag, Trash2, Minus, Plus } from "lucide-react";

export const CartDrawer = () => {
  const { items, remove, setQty, totalCents, open, setOpen, clear } = useCart();
  const { t } = useLang();
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    setLoading(true);
    try {
      const res = await api.post("/store/checkout", {
        items: items.map((i) => ({ slug: i.slug, quantity: i.quantity, size: i.size || null, color: i.color || null })),
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch {
      toast.error("Checkout error");
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[1200]" data-testid="cart-drawer">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)} />
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-[#0B0C0E] border-l border-[#272A34] flex flex-col shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-[#272A34]">
          <h2 className="font-serif text-xl text-amber-100 flex items-center gap-2"><ShoppingBag className="w-5 h-5 text-amber-500" /> {t("cart")}</h2>
          <button data-testid="cart-close" onClick={() => setOpen(false)} className="p-1.5 text-stone-400 hover:text-amber-400"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {items.length === 0 ? (
            <p className="text-stone-400 text-center py-16" data-testid="cart-empty">{t("cart_empty")}</p>
          ) : items.map((i) => (
            <div key={i.key} className="flex gap-3 bg-[#121418] border border-[#272A34] rounded-lg p-3" data-testid={`cart-item-${i.slug}-${i.color || "nocolor"}-${i.size || "nosize"}`}>
              <img src={i.image} alt="" className="w-16 h-16 rounded object-cover" />
              <div className="flex-1 min-w-0">
                <div className="font-serif text-sm text-stone-100 truncate">{i.name}{(i.color || i.size) ? <span className="text-amber-400/80"> · {[i.color, i.size].filter(Boolean).join(" / ")}</span> : null}</div>
                <div className="text-amber-400 text-sm font-mono mt-0.5">{formatPrice(i.price_cents)}</div>
                <div className="flex items-center gap-2 mt-2">
                  <button onClick={() => setQty(i.key, i.quantity - 1)} className="w-6 h-6 rounded bg-[#22252D] text-stone-300 flex items-center justify-center"><Minus className="w-3 h-3" /></button>
                  <span className="text-sm text-stone-200 w-6 text-center font-mono">{i.quantity}</span>
                  <button onClick={() => setQty(i.key, i.quantity + 1)} className="w-6 h-6 rounded bg-[#22252D] text-stone-300 flex items-center justify-center"><Plus className="w-3 h-3" /></button>
                  <button data-testid={`cart-remove-${i.slug}-${i.color || "nocolor"}-${i.size || "nosize"}`} onClick={() => remove(i.key)} className="ml-auto text-stone-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {items.length > 0 && (
          <div className="p-5 border-t border-[#272A34] space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-stone-400">{t("cart_total")}</span>
              <span className="font-serif text-2xl text-amber-100" data-testid="cart-total">{formatPrice(totalCents)}</span>
            </div>
            <button data-testid="cart-checkout-btn" onClick={checkout} disabled={loading} className="w-full bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-stone-950 font-semibold px-6 py-3.5 rounded-md shadow-lg transition-all disabled:opacity-60 border border-amber-400/30">
              {loading ? "…" : t("cart_checkout")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartDrawer;
